import { useMemo } from 'react';
import { getAllLocalPageSlugs } from '../data/localPageData';

export interface MenuItem {
  key: string;
  label: string;
  href?: string;
  type?: 'link' | 'primary';
  icon?: string;
  children?: MenuItem[];
}

export interface MenuCategory {
  key: string;
  label: string;
  icon: string;
  children: MenuItem[];
}

// Helper function to get appropriate icon for each page
const getIconForPage = (slug: string): string => {
  const iconMap: Record<string, string> = {
    about: 'UserOutlined',
    testimonials: 'StarOutlined',
    team: 'TeamOutlined',
    history: 'ClockCircleOutlined',
    mission: 'FlagOutlined',
    services: 'SolutionOutlined',
    programs: 'BookOutlined',
    admissions: 'IdcardOutlined',
    therapy: 'HeartOutlined',
    education: 'ReadOutlined',
    training: 'TrophyOutlined',
    news: 'FileTextOutlined',
    gallery: 'PictureOutlined',
    events: 'CalendarOutlined',
    blog: 'EditOutlined',
    announcements: 'NotificationOutlined',
    achievements: 'TrophyOutlined',
    contact: 'ContactsOutlined',
    location: 'EnvironmentOutlined',
    enquiry: 'QuestionCircleOutlined',
    support: 'CustomerServiceOutlined'
  };
  
  return iconMap[slug] || 'FileTextOutlined';
};

export const useDynamicMenu = (): { navigationItems: MenuItem[]; menuCategories: MenuCategory[]; loginItem: MenuItem } => {
  const navigationItems = useMemo(() => {
    // Core navigation items (always visible)
    const coreItems: MenuItem[] = [
      { 
        key: 'home', 
        label: 'Home', 
        href: '/', 
        icon: 'HomeOutlined'
      }
    ];

    return coreItems;
  }, []);

  const loginItem = useMemo((): MenuItem => {
    return { 
      key: 'login', 
      label: 'Login', 
      href: '/login', 
      type: 'primary' as const, 
      icon: 'LoginOutlined'
    };
  }, []);

  const menuCategories = useMemo(() => {
    const allSlugs = getAllLocalPageSlugs();
    
    // Filter out admin and system pages
    const userPages = allSlugs.filter(slug => 
      !['admin', 'cms', 'dashboard', 'login', 'signup', 'home'].includes(slug)
    );

    // Auto-generate categories based on available pages
    const categoryMap = new Map<string, string[]>();
    
    // Automatically categorize pages based on their content type or purpose
    userPages.forEach(slug => {
      if (['about', 'testimonials', 'team', 'history', 'mission'].includes(slug)) {
        if (!categoryMap.has('about')) categoryMap.set('about', []);
        categoryMap.get('about')!.push(slug);
      } else if (['services', 'programs', 'admissions', 'therapy', 'education', 'training'].includes(slug)) {
        if (!categoryMap.has('services')) categoryMap.set('services', []);
        categoryMap.get('services')!.push(slug);
      } else if (['news', 'gallery', 'events', 'blog', 'announcements', 'achievements'].includes(slug)) {
        if (!categoryMap.has('information')) categoryMap.set('information', []);
        categoryMap.get('information')!.push(slug);
      } else if (['contact', 'location', 'enquiry', 'support'].includes(slug)) {
        if (!categoryMap.has('contact')) categoryMap.set('contact', []);
        categoryMap.get('contact')!.push(slug);
      } else {
        // Create a "general" category for uncategorized pages
        if (!categoryMap.has('general')) categoryMap.set('general', []);
        categoryMap.get('general')!.push(slug);
      }
    });

    // Convert category map to menu categories
    const categories: MenuCategory[] = Array.from(categoryMap.entries()).map(([categoryKey, pageSlugs]) => {
      // Get appropriate icon and label for each category
      const categoryConfig = {
        about: { label: 'About', icon: 'InfoCircleOutlined' },
        services: { label: 'Services', icon: 'BookOutlined' },
        information: { label: 'Information', icon: 'FileTextOutlined' },
        contact: { label: 'Contact', icon: 'ContactsOutlined' },
        general: { label: 'More', icon: 'AppstoreOutlined' }
      };

      const config = categoryConfig[categoryKey as keyof typeof categoryConfig] || 
                    { label: categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1), icon: 'FileTextOutlined' };

      return {
        key: categoryKey,
        label: config.label,
        icon: config.icon,
        children: pageSlugs.map(slug => ({
          key: slug,
          label: slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' '),
          href: `/${slug}`,
          icon: getIconForPage(slug)
        }))
      };
    });

    // Filter out empty categories and sort by category order
    return categories
      .filter(category => category.children.length > 0)
      .sort((a, b) => {
        const order = { about: 1, services: 2, information: 3, contact: 4, general: 5 };
        return (order[a.key as keyof typeof order] || 999) - (order[b.key as keyof typeof order] || 999);
      });
  }, []);

  return { navigationItems, menuCategories, loginItem };
};
